import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutTemplate, Plus, Trash2, RotateCcw, Save, ListChecks, SlidersHorizontal, HardHat, Layers, Check, Tag, X, ShieldAlert, Sparkles, Copy } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner } from "@/components/tac";
import { tradeLabel } from "@/lib/trades";

const FIELD_TYPES = ["text", "number", "bool"];
const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";

function IndustryPacks({ verticals, onApply }) {
  const active = verticals?.active;
  return (
    <div className="card-tac p-5" data-testid="industry-packs">
      <div className="mb-1 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Layers className="h-4 w-4 text-amber-400" /> Industry packs</div>
      <p className="mb-4 text-xs text-slate-500">Apply a pre-built vertical to instantly set your featured trades, trade-native terminology and cost tags. Built for commercial & sub-trade operations.</p>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {(verticals?.packs || []).map((p) => {
          const isActive = active === p.slug;
          return (
            <div key={p.slug} data-testid={`vertical-card-${p.slug}`}
              className={`flex flex-col rounded-xl border p-4 transition-colors ${isActive ? "border-amber-500/60 bg-amber-500/5" : "border-slate-800 bg-[#0d1420]"}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display text-sm font-semibold text-white">{p.label}</h3>
                {isActive && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono-tac text-[9px] uppercase text-amber-300">Active</span>}
              </div>
              <p className="mt-1 flex-1 text-xs text-slate-400">{p.tagline}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {p.trades.slice(0, 5).map((t) => <span key={t} className="rounded-md bg-slate-800/70 px-1.5 py-0.5 font-mono-tac text-[9px] text-slate-400">{tradeLabel(t)}</span>)}
              </div>
              <button data-testid={`vertical-apply-${p.slug}`} onClick={() => onApply(p.slug)} disabled={isActive}
                className={`mt-3 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-xs ${isActive ? "cursor-default border border-slate-700 text-slate-500" : "btn-amber"}`}>
                {isActive ? <><Check className="h-3.5 w-3.5" /> Applied</> : "Apply pack"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CustomFields({ initial, onSaved }) {
  const [tags, setTags] = useState(initial.line_item_tags || []);
  const [tagDraft, setTagDraft] = useState("");
  const [equip, setEquip] = useState(initial.equipment_id_label || "");
  const [subNote, setSubNote] = useState(initial.subcontractor_note_label || "");
  const [jobFields, setJobFields] = useState((initial.custom_job_fields || []).map((f) => ({ ...f })));
  const [saving, setSaving] = useState(false);

  const addTag = () => { const v = tagDraft.trim(); if (v && !tags.includes(v)) setTags([...tags, v]); setTagDraft(""); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/workspace/custom-fields", {
        line_item_tags: tags, equipment_id_label: equip, subcontractor_note_label: subNote,
        custom_job_fields: jobFields.filter((f) => f.key.trim() && f.label.trim()),
      });
      toast.success("Custom fields saved");
      onSaved?.(data.custom_fields);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="card-tac p-5" data-testid="custom-fields">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Tag className="h-4 w-4 text-cyan-400" /> Trade-specific custom fields</div>
        <button data-testid="save-custom-fields-btn" disabled={saving} onClick={save} className="btn-amber inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs disabled:opacity-50"><Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}</button>
      </div>

      <label className="text-xs text-slate-400">Line-item cost tags <span className="text-slate-600">(used by job-costing & quotes)</span></label>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="cost-tags">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-[#0d1420] px-2.5 py-1 text-xs text-slate-200">
            {t}<button onClick={() => setTags(tags.filter((x) => x !== t))} className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {!tags.length && <span className="text-xs text-slate-600">No tags yet.</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <input data-testid="cost-tag-input" value={tagDraft} onChange={(e) => setTagDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag e.g. Permit" className={`${inp} max-w-xs`} />
        <button data-testid="cost-tag-add" onClick={addTag} className="rounded-lg border border-slate-700 px-3 text-xs text-cyan-400 hover:border-cyan-500">Add</button>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs text-slate-400">Equipment / asset ID label</label>
          <input data-testid="equip-label-input" value={equip} onChange={(e) => setEquip(e.target.value)} placeholder="Serial / model #" className={`${inp} mt-1`} />
        </div>
        <div>
          <label className="text-xs text-slate-400">Subcontractor note label</label>
          <input data-testid="subnote-label-input" value={subNote} onChange={(e) => setSubNote(e.target.value)} placeholder="Subcontractor notes" className={`${inp} mt-1`} />
        </div>
      </div>

      <div className="mt-5">
        <label className="text-xs text-slate-400">Custom job fields</label>
        <div className="mt-2 space-y-2">
          {jobFields.map((f, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`cjf-row-${i}`}>
              <input placeholder="key" value={f.key} onChange={(e) => setJobFields(jobFields.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} className={`${inp} w-32`} />
              <input placeholder="Label" value={f.label} onChange={(e) => setJobFields(jobFields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={inp} />
              <select value={f.type || "text"} onChange={(e) => setJobFields(jobFields.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} className={`${inp} w-28`}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={() => setJobFields(jobFields.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!jobFields.length && <p className="text-xs text-slate-600">No custom job fields.</p>}
        </div>
        <button data-testid="add-cjf-btn" onClick={() => setJobFields([...jobFields, { key: "", label: "", type: "text" }])} className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"><Plus className="h-3.5 w-3.5" /> Add custom field</button>
      </div>
    </div>
  );
}

function MarginPolicy({ verticals }) {
  const { data } = useQuery({ queryKey: ["margin-policy"], queryFn: async () => (await api.get("/workspace/margin-policy")).data });
  const [min, setMin] = useState("");
  const [enforce, setEnforce] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [floors, setFloors] = useState({});
  useEffect(() => { if (data && !loaded) { setMin(data.min_margin_pct ?? ""); setEnforce(!!data.enforce); setFloors(data.floors || {}); setLoaded(true); } }, [data, loaded]);
  const activeSlug = verticals?.active;
  const activePack = (verticals?.packs || []).find((p) => p.slug === activeSlug);
  const presets = activePack?.margin_floor_presets || {};
  const applyPresets = () => { setFloors((f) => ({ ...presets, ...f })); toast.success("Suggested floors filled - review & Save"); };
  const overwritePresets = () => { setFloors({ ...floors, ...presets }); toast.success("Suggested floors applied - review & Save"); };
  const tradeKeys = Array.from(new Set(["av_low_voltage", "electrical", "hvac", "plumbing", ...Object.keys(presets)]));
  const save = async () => {
    try {
      const fl = Object.fromEntries(Object.entries(floors).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
      await api.put("/workspace/margin-policy", { min_margin_pct: min === "" ? null : Number(min), enforce, floors: fl }); toast.success("Margin guardrail saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="card-tac p-5" data-testid="margin-policy">
      <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><ShieldAlert className="h-4 w-4 text-rose-400" /> Margin guardrail</div>
      <p className="mb-4 text-xs text-slate-500">Block locking an invoice to QuickBooks / Xero when a job's gross margin falls below your minimum. Owners can override case-by-case at closeout.</p>
      {Object.keys(presets).length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="floor-presets">
          <span className="font-mono-tac text-[10px] uppercase tracking-widest text-amber-300">Suggested floors · {activePack?.label}</span>
          {Object.entries(presets).map(([t, v]) => <span key={t} className="rounded-md bg-slate-800/70 px-1.5 py-0.5 font-mono-tac text-[9px] text-slate-300">{t.replace(/_/g, " ")} {v}%</span>)}
          <button data-testid="apply-floor-presets-btn" onClick={applyPresets} className="ml-auto rounded-full border border-amber-500/60 px-3 py-1 text-[11px] text-amber-300 hover:bg-amber-500/10">Fill empty</button>
          <button data-testid="overwrite-floor-presets-btn" onClick={overwritePresets} className="rounded-full border border-slate-700 px-3 py-1 text-[11px] text-slate-300 hover:border-amber-500">Use all</button>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs text-slate-400">Minimum gross margin %</label>
          <input data-testid="margin-min-input" type="number" value={min} onChange={(e) => setMin(e.target.value)} placeholder="e.g. 30" className={`${inp} mt-1 w-40`} />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-300">
          <input data-testid="margin-enforce-toggle" type="checkbox" checked={enforce} onChange={(e) => setEnforce(e.target.checked)} className="accent-rose-500" /> Enforce (block low-margin invoices)
        </label>
        <div className="w-full">
          <div className="mb-1 text-xs text-slate-400">Per-trade floors (override the minimum for that job type)</div>
          <div className="flex flex-wrap gap-3">
            {tradeKeys.map((t) => (
              <label key={t} className="flex items-center gap-1.5 text-xs text-slate-400">{t.replace(/_/g, " ")}
                <input data-testid={`floor-${t}`} type="number" value={floors[t] ?? ""} onChange={(e) => setFloors({ ...floors, [t]: e.target.value })} placeholder="%" className={`${inp} w-16`} /></label>
            ))}
          </div>
        </div>
        <button data-testid="margin-save-btn" onClick={save} className="btn-amber inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"><Save className="h-3.5 w-3.5" /> Save</button>
      </div>
    </div>
  );
}

function DemoSeed() {
  return null; // Demo sample-quote generator removed - workspaces start blank.
}

export default function Templates() {
  const { user, refreshMe } = useAuth();
  const { data: templates, isLoading, refetch } = useQuery({
    queryKey: ["trade-templates"], queryFn: async () => (await api.get("/trade-templates")).data });
  const { data: verticals, refetch: refetchVerticals } = useQuery({
    queryKey: ["verticals"], queryFn: async () => (await api.get("/verticals")).data });
  const [active, setActive] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [fields, setFields] = useState([]);
  const [pretaskItems, setPretaskItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (templates && !active) load(templates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const load = (t) => { setActive(t.trade); setChecklist([...(t.checklist || [])]); setFields((t.system_state_fields || []).map((f) => ({ ...f }))); setPretaskItems([...(t.pretask_items || [])]); };
  const current = (templates || []).find((t) => t.trade === active);

  const applyPack = async (slug) => {
    try {
      await api.post("/verticals/apply", { vertical: slug });
      toast.success("Industry pack applied");
      await Promise.all([refetchVerticals(), refetch(), refreshMe()]);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/trade-templates/${active}`, { checklist: checklist.filter((c) => c.trim()), system_state_fields: fields.filter((f) => f.key && f.label), pretask_items: pretaskItems.filter((c) => c.trim()) });
      toast.success("Template saved");
      await refetch();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm("Reset this trade template to the built-in default? Your customizations will be lost.")) return;
    try {
      await api.delete(`/trade-templates/${active}`);
      toast.success("Reset to default");
      const { data } = await api.get(`/trade-templates/${active}`);
      setChecklist([...data.checklist]); setFields(data.system_state_fields.map((f) => ({ ...f }))); setPretaskItems([...(data.pretask_items || [])]);
      await refetch();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (user && user.role !== "owner") return <Navigate to="/dispatch" replace />;
  if (isLoading) return <div className="p-10"><Spinner label="Loading templates" /></div>;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div><Eyebrow>Owner configuration</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Industry & template setup</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">Pick your industry pack, define trade-specific custom fields, then fine-tune each trade's checklist and capture fields.</p></div>

      <div className="mt-8 space-y-6">
        {verticals && <IndustryPacks verticals={verticals} onApply={applyPack} />}
        {verticals && <CustomFields key={verticals.active || "none"} initial={verticals.custom_fields} onSaved={() => refetchVerticals()} />}
        <div className="grid gap-6 lg:grid-cols-2"><MarginPolicy verticals={verticals} /><DemoSeed /></div>
      </div>

      <h2 className="mt-10 font-display text-xl font-semibold text-white">Trade templates</h2>
      <div className="mt-4 grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Trade list */}
        <div className="card-tac h-fit divide-y divide-slate-800 p-1" data-testid="trade-list">
          {(templates || []).map((t) => (
            <button key={t.trade} data-testid={`trade-tab-${t.trade}`} onClick={() => load(t)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${active === t.trade ? "bg-amber-500/10 text-amber-400" : "text-slate-300 hover:bg-slate-800/60"}`}>
              <span className="flex items-center gap-2"><LayoutTemplate className="h-4 w-4" /> {t.label}</span>
              {t.customized && <span className="rounded-full bg-cyan-500/15 px-1.5 py-0.5 font-mono-tac text-[9px] uppercase text-cyan-300">edited</span>}
            </button>
          ))}
        </div>

        {/* Editor */}
        {current && (
          <div className="space-y-6" data-testid="template-editor">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-white">{current.label}</h2>
              <div className="flex gap-2">
                <button data-testid="reset-template-btn" onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-rose-500"><RotateCcw className="h-3.5 w-3.5" /> Reset default</button>
                <button data-testid="save-template-btn" disabled={saving} onClick={save} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs disabled:opacity-50"><Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save template"}</button>
              </div>
            </div>

            {/* Checklist */}
            <div className="card-tac p-5">
              <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><ListChecks className="h-4 w-4 text-amber-400" /> Field checklist</div>
              <div className="space-y-2">
                {checklist.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`checklist-item-${i}`}>
                    <span className="font-mono-tac text-[10px] text-slate-600 w-5">{i + 1}</span>
                    <input value={c} onChange={(e) => setChecklist(checklist.map((x, j) => (j === i ? e.target.value : x)))} className={inp} data-testid={`checklist-input-${i}`} />
                    <button onClick={() => setChecklist(checklist.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400" data-testid={`checklist-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {!checklist.length && <p className="text-sm text-slate-600">No checklist items.</p>}
              </div>
              <button data-testid="add-checklist-btn" onClick={() => setChecklist([...checklist, ""])} className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"><Plus className="h-3.5 w-3.5" /> Add checklist item</button>
            </div>

            {/* System-state fields */}
            <div className="card-tac p-5">
              <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><SlidersHorizontal className="h-4 w-4 text-cyan-400" /> System-state fields</div>
              <div className="space-y-2">
                {fields.map((f, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`field-row-${i}`}>
                    <input placeholder="key" value={f.key} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} className={`${inp} w-32`} data-testid={`field-key-${i}`} />
                    <input placeholder="Label shown to tech" value={f.label} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={inp} data-testid={`field-label-${i}`} />
                    <select value={f.type || "text"} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} className={`${inp} w-28`} data-testid={`field-type-${i}`}>
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button onClick={() => setFields(fields.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400" data-testid={`field-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {!fields.length && <p className="text-sm text-slate-600">No system-state fields.</p>}
              </div>
              <button data-testid="add-field-btn" onClick={() => setFields([...fields, { key: "", label: "", type: "text" }])} className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"><Plus className="h-3.5 w-3.5" /> Add field</button>
            </div>

            {/* Trade-specific pre-task safety items */}
            <div className="card-tac p-5">
              <div className="mb-1 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><HardHat className="h-4 w-4 text-rose-400" /> Pre-task safety items</div>
              <p className="mb-3 text-xs text-slate-500">Added on top of the standard OSHA pre-task checklist for this trade. The crew must acknowledge all of these before starting work.</p>
              <div className="space-y-2">
                {pretaskItems.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`pretask-item-${i}`}>
                    <span className="font-mono-tac text-[10px] text-slate-600 w-5">{i + 1}</span>
                    <input value={c} onChange={(e) => setPretaskItems(pretaskItems.map((x, j) => (j === i ? e.target.value : x)))} className={inp} data-testid={`pretask-input-${i}`} />
                    <button onClick={() => setPretaskItems(pretaskItems.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400" data-testid={`pretask-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {!pretaskItems.length && <p className="text-sm text-slate-600">No trade-specific safety items. Standard OSHA items still apply.</p>}
              </div>
              <button data-testid="add-pretask-btn" onClick={() => setPretaskItems([...pretaskItems, ""])} className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300"><Plus className="h-3.5 w-3.5" /> Add safety item</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
